import { useNavigate } from 'react-router-dom'
import { Table, Button, Message } from '@bitcine/cinesend-theme/dist'
import AddModal from './add_modal'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '../../hooks/use_snackbar'
import axios from 'axios'
import useTableFilters from '../../hooks/use_table_filters'

const ItemsTable = ({ category, invalidateKey, itemsKey, langID }) => {
  const { addMessage } = useSnackbar()
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedIDs, setSelectedIDs] = useState([])

  const mapTableToItemsKey = () => {
    if (itemsKey === 'category_items') {
      if (category.type === 'categories') {
        return 'child_categories'
      }

      return 'category_items'
    }

    return itemsKey
  }
  const internalItemsKey = mapTableToItemsKey()

  const isFeaturedContent = itemsKey === 'featured_content'

  const navigate = useNavigate()

  let queryKey = `categories/${category.id}/get-addable-items?type=${['warning-slates', 'featured-content', 'ad-pools'].includes(showModal) ? 'assets' : showModal}`

  if ((showModal === 'warning-slates' || showModal === 'ad-pools') && itemsKey === 'category_items') {
    queryKey += `&adAssignment`
  }

  if (isFeaturedContent) {
    queryKey += `&featuredContentAssignment`
  }

  const itemsQueryKey = `categories/${category.id}/get-items?type=${itemsKey}`

  const sortingOptions = [
    { key: 'title', label: 'Name', direction: 'asc' },
    { key: 'start_date', label: 'Start Date', direction: 'asc' },
    { key: 'end_date', label: 'End Date', direction: 'asc' },
  ]

  const { searching, pagination, queryString } = useTableFilters({
    itemsQueryKey,
    sortingOptions,
    enableSearch: true,
    initialSort: category.enforce_az_order ? { key: 'title', direction: 'asc' } : {},
    queryExists: true,
  })

  const updateOrder = useMutation(
    ({ ids, startIndex }) => {
      return axios.put(`/categories/${category.id}/update-item-order`, {
        ids: ids,
        startIndex: startIndex,
        type: category.type,
        isFeaturedContent,
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryString)
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  const removeItems = useMutation(
    () =>
      axios.delete(`/categories/${category.id}/remove-items`, {
        data: { ids: selectedIDs, type: category.type, isFeaturedContent },
      }),
    {
      onSuccess: () => {
        setSelectedIDs([])
        queryClient.invalidateQueries(queryString)
      },
      onError: (err) => {
        addMessage(err?.response?.data?.error ?? err.message, 'error')
      },
    }
  )

  const { data, isLoading, isRefetching, isError, error: errors } = useQuery([itemsQueryKey, queryString])
  const categoryItems = data?.data || []

  return (
    <>
      {category.issues.length > 0 && (
        <Message error={true} small={true} className='mb-2'>
          {category.issues[0]?.description}
        </Message>
      )}
      <Table
        draggable={!category.enforce_az_order && searching.search === ''}
        widths={['auto', '120', '120']}
        status={{
          pending: isLoading || isRefetching,
          error: isError,
          errorMessage: errors?.message,
        }}
        header={{
          columns: [
            { text: 'Name', key: 'title' },
            { text: 'Start Date', key: 'start_date' },
            { text: 'End Date', key: 'end_date' },
          ],
          checkbox: {
            checked: categoryItems.length > 0 && selectedIDs.length === categoryItems.length,
            indeterminate: selectedIDs.length > 0 && selectedIDs.length < categoryItems.length,
            onChange: () => {
              if (selectedIDs.length === categoryItems.length) {
                setSelectedIDs([])
              } else {
                setSelectedIDs(categoryItems.map(({ id }) => id))
              }
            },
          },
          searching,
          customElement: (
            <>
              {(!category.type || category.type === 'assets') && (
                <Button icon='add_circle_outline' onClick={() => setShowModal('assets')}>
                  Add Assets
                </Button>
              )}
              {(!category.type || (category.type === 'categories' && itemsKey !== 'featured_content')) && (
                <Button icon='add_circle_outline' onClick={() => setShowModal('categories')}>
                  Add Categories
                </Button>
              )}
              {(!category.type || category.type === 'warning-slates') && (
                <Button icon='add_circle_outline' onClick={() => setShowModal('warning-slates')}>
                  {itemsKey === 'category_items' ? 'Add Warning Slates' : 'Add Assets'}
                </Button>
              )}
              {(!category.type || category.type === 'ad-pools') && (
                <Button icon='add_circle_outline' onClick={() => setShowModal('ad-pools')}>
                  {itemsKey === 'category_items' ? 'Add Preroll Ads' : 'Add Assets'}
                </Button>
              )}
              {(!category.type || (category.type === 'categories' && itemsKey === 'featured_content')) && (
                <>
                  <Button icon='add_circle_outline' onClick={() => setShowModal('assets')}>
                    Featured Asset
                  </Button>
                  <Button icon='add_circle_outline' onClick={() => setShowModal('categories')}>
                    Featured Category
                  </Button>
                </>
              )}
              {selectedIDs.length > 0 && (
                <Button icon='remove_circle_outline' onClick={() => removeItems.mutate()} type='error'>
                  Remove ({selectedIDs.length}) Items
                </Button>
              )}
            </>
          ),
        }}
        body={{
          data: categoryItems,
          onDragEnd: (order) => {
            let startIndex = ((pagination.currentPage ?? 1) - 1) * pagination.rowsPerPage + 1
            const ids = order.map((item) => item.id)
            updateOrder.mutate({ ids, startIndex })
          },
          row: {
            onClick:
              category.type === 'categories'
                ? (_, data) => {
                    if (isFeaturedContent) {
                      return navigate(
                        `/airlines/${category.airline_id}/cycles/${category.cycle_id}/metadata/featured-content/${data.id}`
                      )
                    }

                    return navigate(
                      `/airlines/${category.airline_id}/cycles/${category.cycle_id}/categories/${data.id}/items`
                    )
                  }
                : null,

            checkbox: {
              checked: (data) => selectedIDs.includes(data.id),
              onChange: (data) =>
                selectedIDs.includes(data.id)
                  ? setSelectedIDs(selectedIDs.filter((i) => i !== data.id))
                  : setSelectedIDs([...selectedIDs, data.id]),
            },
            render: [
              (data) => data?.category_values?.find((v) => v.language_id === langID)?.value || data.title,
              (data) => new Date(data.start_date).toLocaleDateString(),
              (data) => new Date(data.end_date).toLocaleDateString(),
            ],
          },
          empty: {
            title: 'No items found!',
            text: 'Add items to begin.',
            icon: 'date_range',
          },
        }}
        paginate={{ ...pagination, totalRows: data?.meta.total || 0 }}
      />
      {showModal && (
        <AddModal
          category={category}
          invalidateKey={itemsQueryKey}
          onClose={() => setShowModal(false)}
          queryKey={[queryKey]}
          type={showModal}
          langID={langID}
        />
      )}
    </>
  )
}

export default ItemsTable
